import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { loginUser } from '../../features/auth/authSlice';
import { AtSign, Lock, Eye, EyeOff, LogIn, BookOpen } from 'lucide-react';
import { FcGoogle } from 'react-icons/fc';
import { FaFacebook } from 'react-icons/fa';

const SignIn = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error } = useSelector((state: RootState) => state.auth);
  const [formData, setFormData] = useState({
    email: location.state?.email || '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();

  // Show message from redirect if available
  useEffect(() => {
    if (location.state?.message) {
      toast({
        title: "Notice",
        description: location.state.message,
      });
    }
  }, [location.state?.message, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await dispatch(loginUser(formData)).unwrap();
      if (result.statusCode === 200) {
        toast({
          title: "Welcome back!",
          description: result.message,
        });
        navigate('/');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Invalid credentials",
        variant: "destructive",
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row items-stretch bg-muted/30">
      {/* Left side decorative panel - visible on md and larger screens */}
      <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-primary/5 to-primary/20 p-8 items-center justify-center">
        <div className="max-w-md text-center space-y-6 animate-fadeIn">
          <div className="flex justify-center">
            <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
              <BookOpen className="h-10 w-10 text-primary" />
            </div>
          </div>
          <h1 className="text-4xl font-playfair font-bold text-primary">KnowledgeHubster</h1>
          <p className="text-lg text-muted-foreground">
            Your gateway to expert-led courses and comprehensive training solutions.
          </p>
          <div className="mt-8 p-6 bg-white/50 backdrop-blur-sm rounded-lg shadow-sm">
            <blockquote className="italic text-muted-foreground">
              "Education is the passport to the future, for tomorrow belongs to those who prepare for it today."
            </blockquote>
            <p className="mt-2 text-sm font-medium">— Malcolm X</p>
          </div>
        </div>
      </div>

      {/* Right side login form */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-md space-y-6 bg-white p-8 rounded-lg shadow-md border border-muted/40 animate-fadeIn">
          {/* Mobile logo - only visible on small screens */}
          <div className="md:hidden flex justify-center mb-6">
            <div className="flex flex-col items-center">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                <BookOpen className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-2xl font-playfair font-bold text-primary">KnowledgeHubster</h1>
            </div>
          </div>

          <div className="text-center">
            <h2 className="text-3xl font-playfair font-bold tracking-tight">
              Welcome back
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link to="/signup" className="font-medium text-primary hover:text-primary/80 transition-colors">
                Sign up
              </Link>
            </p>
          </div>

          <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium">
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <AtSign className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="pl-10 transition-all duration-200 border-muted hover:border-primary/50 focus:border-primary"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="block text-sm font-medium">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-sm font-medium text-primary hover:text-primary/80 transition-colors hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <Lock className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className="pl-10 pr-10 transition-all duration-200 border-muted hover:border-primary/50 focus:border-primary"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-primary transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center">
                <Checkbox id="remember" className="mr-2 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" />
                <label htmlFor="remember" className="text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                  Remember me
                </label>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 text-base flex items-center justify-center gap-2 group hover:shadow-md transition-all duration-200"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></span>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <LogIn className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                  <span>Sign in</span>
                </>
              )}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-muted"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-muted-foreground">
                  Or continue with
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                type="button"
                className="w-full border-muted hover:bg-muted/10 hover:text-primary transition-all duration-200"
                disabled
              >
                <FcGoogle className="h-5 w-5 mr-2" />
                Google
              </Button>
              <Button
                variant="outline"
                type="button"
                className="w-full border-muted hover:bg-muted/10 hover:text-primary transition-all duration-200"
                disabled
              >
                <FaFacebook className="h-5 w-5 mr-2 text-blue-600" />
                Facebook
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
